import React, { useState } from "react";
import _ from "lodash";
import { useKeyboard } from "./useKeyboard";
import { useGamepad } from "./useGamepad";

/**
 * @typedef {object} MatchObj
 * @property {boolean} win - Indicateur de victoire du match
 * @property {number} point - Point du set
 */

/**
 * @typedef {object} TeamScoreObj
 * @property {string} team - Nom de l'équipe
 * @property {Array<MatchObj>} matchs - Liste des matchs de l'équipe
 */

/**
 * @typedef {object} HistoricScoreObj
 * @property {Array<TeamScoreObj>} scores - Scores des équipes
 * @property {number} match - Match courant
 * @property {string} committedTeam - Équipe au service
 */

/**
 * @typedef {import('../controllers/RpaResourceController').RpaKeyAndPadCenterObj } RpaKeyAndPadCenterObj
 */

/**
 * @typedef {object} RpaScoringFutsalParams
 * @property {RpaKeyAndPadCenterObj} keyAndPadCenterObj - Objet des keycodes et boutons du pad central
 */

/**
 * @param {RpaScoringFutsalParams} props
 */
export default function RpaScoringFutsal({ keyAndPadCenterObj }) {
  const [getCurrentMatch, setCurrentMatch] = useState(0);
  const [getCurrentCommittedTeam, setCurrentCommittedTeam] = useState("a");
  const [getScores, setScores] = useState(
    /** @type {Array<TeamScoreObj>} */ ([
      {
        team: "a",
        matchs: [{ win: false, point: 0 }]
      },
      {
        team: "b",
        matchs: [{ win: false, point: 0 }]
      }
    ])
  );
  const [getHistoricScores, setHistoricScores] = useState(
    /** @type {Array<HistoricScoreObj>} */ ([
      {
        scores: [
          {
            team: "a",
            matchs: [{ win: false, point: 0 }]
          },
          {
            team: "b",
            matchs: [{ win: false, point: 0 }]
          }
        ],
        match: 0,
        committedTeam: "a"
      }
    ])
  );

  useGamepad(buttonIndex => {
    if (buttonIndex === keyAndPadCenterObj.padCodes.addPointTeamA) _onClickOnAddPoint("a");
    if (buttonIndex === keyAndPadCenterObj.padCodes.addPointTeamB) _onClickOnAddPoint("b");
    if (buttonIndex === keyAndPadCenterObj.padCodes.backScoring) _onClickOnBackPoint();
    if (buttonIndex === keyAndPadCenterObj.padCodes.resetScoring) _clear();
    if (buttonIndex === keyAndPadCenterObj.padCodes.addMatch) _onClickOnAddMatch();
  });

  useKeyboard({
    [keyAndPadCenterObj.keyCodes.addPointTeamA]: () => _onClickOnAddPoint("a"),
    [keyAndPadCenterObj.keyCodes.addPointTeamB]: () => _onClickOnAddPoint("b"),
    [keyAndPadCenterObj.keyCodes.resetScoring]: () => _clear(),
    [keyAndPadCenterObj.keyCodes.backScoring]: () => _onClickOnBackPoint(),
    [keyAndPadCenterObj.keyCodes.addMatch]: () => _onClickOnAddMatch()
  });

  /**
   * Foncion de remise à zéro des scores
   * @private
   */
  function _clear() {
    setHistoricScores([
      {
        scores: [
          {
            team: "a",
            matchs: [{ win: false, point: 0 }]
          },
          {
            team: "b",
            matchs: [{ win: false, point: 0 }]
          }
        ],
        match: 0,
        committedTeam: "a"
      }
    ]);
    setScores([
      {
        team: "a",
        matchs: [{ win: false, point: 0 }]
      },
      {
        team: "b",
        matchs: [{ win: false, point: 0 }]
      }
    ]);
    setCurrentMatch(0);
    setCurrentCommittedTeam("a");
  }

  /**
   * Gestion du clic sur l'ajout d'un point dans l'historique des scores
   * @param {string} teamStr - Chaîne du team
   * @private
   */
  function _onClickOnAddPoint(teamStr) {
    var scores = getScores.map(score => {
      var match = score.matchs[getCurrentMatch];

      // NOTE on incrémente le point du set courant de l'équipe concernée
      if (score.team === teamStr) {
        match = _addPointOnMatch(match);

        // NOTE on change l'équipe qui engage
        setCurrentCommittedTeam(teamStr === "a" ? "b" : "a");
      }

      return score;
    });

    setScores(scores);
    setHistoricScores([
      ..._.cloneDeep(getHistoricScores),
      {
        scores: _.cloneDeep(scores),
        match: getCurrentMatch,
        committedTeam: getCurrentCommittedTeam
      }
    ]);
  }

  /**
   * Gestion du clic sur le retour en arrière dans l'historique des scores
   * @private
   */
  function _onClickOnBackPoint() {
    if (getHistoricScores.length > 1) {
      var historicScores = _.cloneDeep(getHistoricScores);
      historicScores.pop();
      var previousScores = _.cloneDeep(historicScores[historicScores.length - 1].scores);
      setHistoricScores(historicScores);
      setScores(previousScores);
      setCurrentMatch(historicScores[historicScores.length - 1].match);
      setCurrentCommittedTeam(historicScores[historicScores.length - 1].committedTeam);
    }
  }

  /**
   * Gestion du clic sur l'ajout d'un match
   * @private
   */
  function _onClickOnAddMatch() {
    var currentMatch = getCurrentMatch,
      scores = getScores,
      teamAPoint = scores[0].matchs[currentMatch].point,
      teamBPoint = scores[1].matchs[currentMatch].point;

    if (teamAPoint === 0 && teamBPoint === 0) {
      return;
    }

    scores = teamAPoint === teamBPoint ? _endMatchWithEquality(scores, currentMatch) : _endMatchWithWinner(scores, currentMatch);

    setScores(scores);
    setCurrentMatch(getCurrentMatch + 1);
  }

  /**
   * Permet de terminer un match avec une égalité
   * @param {Array<TeamScoreObj>} scores - Liste des scores
   * @param {number} currentMatch - Match courant
   * @returns {Array<TeamScoreObj>}
   * @private
   */
  function _endMatchWithEquality(scores, currentMatch) {
    scores[0].matchs[currentMatch].win = true;
    scores[1].matchs[currentMatch].win = true;
    scores[0].matchs.push({ win: false, point: 0 });
    scores[1].matchs.push({ win: false, point: 0 });
    return scores;
  }

  /**
   * Permet de terminer un match avec un gagnant
   * @param {Array<TeamScoreObj>} scores - Liste des scores
   * @param {number} currentMatch - Match courant
   * @returns {Array<TeamScoreObj>}
   * @private
   */
  function _endMatchWithWinner(scores, currentMatch) {
    var teamAPoint = scores[0].matchs[currentMatch].point,
      teamBPoint = scores[1].matchs[currentMatch].point,
      teamMatchWin = teamAPoint > teamBPoint ? "a" : "b";

    switch (teamMatchWin) {
      case "a":
        scores[0].matchs[currentMatch].win = true;
        break;
      case "b":
        scores[1].matchs[currentMatch].win = true;
        break;
    }

    scores[0].matchs.push({ win: false, point: 0 });
    scores[1].matchs.push({ win: false, point: 0 });

    return scores;
  }

  /**
   * Permet d'ajouter un point au match donné
   * @param {MatchObj} match - Match auquel on ajoute un point
   * @returns {MatchObj}
   * @private
   */
  function _addPointOnMatch(match) {
    match.point = match.point + 1;
    return match;
  }

  /**
   * Rendu du point de l'équipe pour le set donné
   * @param {string} teamStr - Nom de l'équipe
   * @returns {JSX.Element} - Élément JSX du point de l'équipe pour le set donné
   */
  function renderTeamPoint(teamStr) {
    var scores = getScores,
      teamScore = /** @type {TeamScoreObj} */ (scores.find(score => score.team === teamStr)),
      currentMatch = getCurrentMatch,
      winningMatchs = teamScore.matchs.filter(match => match.win).length;

    return (
      <div className={`rpascoring__score rpascoring__score--${teamStr}`}>
        {getCurrentCommittedTeam === teamStr ? <span className="rpascoring__score-serv material-symbols-outlined">sports_and_outdoors</span> : ""}
        <div className="rpascoring__score-wins">
          {Array.from({ length: winningMatchs }).map((_, index) => (
            <span key={index} className="rpascoring__score-win material-symbols-outlined">
              trophy
            </span>
          ))}
        </div>
        <div className="rpascoring__score-point">{teamScore.matchs[currentMatch].point}</div>
      </div>
    );
  }

  /**
   * Rendu du tableau des scores
   * @returns {JSX.Element} - Élément JSX du tableau des scores
   */
  function renderTableScore() {
    return (
      <>
        <div className="rpascoring__scores">
          {renderTeamPoint("a")}
          {renderTeamPoint("b")}
        </div>
      </>
    );
  }

  return (
    <div className="scoringsport">
      <div className="scoringsport__actions">
        <button className="scoringsport__action scoringsport__action--a" onClick={_onClickOnAddPoint.bind(null, "a")}>
          +1
        </button>
        <button className="scoringsport__action" onClick={_onClickOnBackPoint.bind(null)}>
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <button className="scoringsport__action" onClick={_onClickOnAddMatch.bind(null)}>
          <span className="material-symbols-outlined">sports_score</span>
        </button>
        <button className="scoringsport__action scoringsport__action--b" onClick={_onClickOnAddPoint.bind(null, "b")}>
          +1
        </button>
      </div>
      {renderTableScore()}
    </div>
  );
}
